import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sendingDomainContract } from "@common/contracts/sending-domain.contract";
import type {
    CreateSendingDomainRequest,
    SendingDomainResponse,
} from "@common/schemas/sending-domain.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { sendingEmailQueryKeys } from "./sendingEmail.api";

const sendingDomainClient = getBackendAPIClient(sendingDomainContract);

const sendingDomainQueryKeys = {
    all: () => ["sending-domains"] as const,
    workspace: (workspaceId: string) =>
        [...sendingDomainQueryKeys.all(), workspaceId] as const,
};

function requireWorkspaceId(workspaceId: string | null | undefined): string {
    if (!workspaceId) {
        throw new Error("No active workspace");
    }
    return workspaceId;
}

export function useSendingDomains(options?: { enabled?: boolean }) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId
            ? sendingDomainQueryKeys.workspace(workspaceId)
            : sendingDomainQueryKeys.all(),
        queryFn: async (): Promise<SendingDomainResponse[]> => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            const result = await sendingDomainClient.listDomains({
                params: { workspaceId: activeWorkspaceId },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load sending domains (status ${result.status})`,
                );
            }
            return result.body.domains;
        },
        enabled: Boolean(workspaceId) && (options?.enabled ?? true),
    });
}

export function useCreateSendingDomain() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: CreateSendingDomainRequest,
        ): Promise<SendingDomainResponse> => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            const result = await sendingDomainClient.createDomain({
                params: { workspaceId: activeWorkspaceId },
                body,
            });
            if (result.status !== 201) {
                throw new Error(
                    `Failed to create sending domain (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: sendingDomainQueryKeys.workspace(workspaceId),
                });
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.all(),
                });
            }
        },
    });
}

export function useVerifySendingDomain() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            domainId: string,
        ): Promise<SendingDomainResponse> => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            const result = await sendingDomainClient.verifyDomain({
                params: { workspaceId: activeWorkspaceId, domainId },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to verify sending domain (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: sendingDomainQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}

export function useDeleteSendingDomain() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (domainId: string) => {
            const activeWorkspaceId = requireWorkspaceId(workspaceId);
            await sendingDomainClient.deleteDomain({
                params: { workspaceId: activeWorkspaceId, domainId },
            });
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: sendingDomainQueryKeys.workspace(workspaceId),
                });
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.all(),
                });
            }
        },
    });
}
